import { Button, Card, message } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClose, AiOutlineFile, AiOutlineUpload } from "react-icons/ai";
import { uploadDoc } from "../../services/upload.service";
import { SpellCheckTextArea } from "../spellcheck";

const MAX_FILES = 15;

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "audio/mpeg",
  "audio/mp3",
  "video/mp4",
  "audio/wav",
  "audio/webm",
];

const folderAndType = (file) => {
  if (file.type === "application/pdf") return ["pdf", "PDF"];
  if (file.type.startsWith("video/")) return ["video", "VIDEO"];
  if (file.type.startsWith("audio/")) {
    const name = file.name.toLowerCase();
    const isVoice = name.endsWith(".wav") || name.endsWith(".webm");
    return ["audio", isVoice ? "VOICE" : "AUDIO"];
  }
  return ["images", "IMAGE"];
};

/** Text box + attachment picker. Calls `onSend({ message, media })`. */
const DiaryComposer = ({ onSend }) => {
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [text, setText] = useState("");
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    if (docs.length > 0 && inputRef.current) inputRef.current.focus();
  }, [docs]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    try {
      const uploaded = [];
      for (const file of files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          message.error(
            `Invalid file type for ${file.name}. Please upload PDF, JPG, PNG, MP3, MP4, or audio files only.`,
          );
          continue;
        }
        const [folder, type] = folderAndType(file);
        const res = await uploadDoc({ file }, folder);
        if (res?.files?.name && res?.id) {
          uploaded.push({ name: res.files.name, id: res.id, type });
        }
      }
      message.success("Files Upload Success");
      setDocs((prev) => [...prev, ...uploaded].slice(0, MAX_FILES));
    } catch (error) {
      console.error(error);
      message.error("Files Upload Failed");
    }
  };

  const canSend = text.trim().length > 0 || docs.length > 0;

  const submit = async () => {
    if (!canSend) return;
    const sent = await onSend({ message: text.trim() || null, media: docs });
    if (sent) {
      setText("");
      setDocs([]);
    }
  };

  return (
    <div>
      {docs.length > 0 && (
        <div className="flex overflow-auto" style={{ marginLeft: "40px" }}>
          {docs.map((doc, index) => (
            <Card
              key={doc.id}
              size="small"
              className="mb-2"
              styles={{
                body: {
                  padding: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                },
              }}
            >
              <div className="flex items-center">
                <AiOutlineFile style={{ marginRight: "4px" }} />
                <span className="truncate">{doc.name}</span>
              </div>
              <Button
                type="text"
                icon={<AiOutlineClose style={{ marginLeft: "12px" }} />}
                onClick={() => {
                  setDocs((old) => old.filter((_, i) => i !== index));
                  message.success("File Removed");
                }}
                style={{ marginLeft: "auto" }}
              />
            </Card>
          ))}
        </div>
      )}
      <div className="flex items-center gap-4">
        <div className="flex flex-col" style={{ gap: "10px" }}>
          <AiOutlineUpload
            size={25}
            className="cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          />
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: "none" }}
            multiple
            accept=".pdf, .jpeg, .jpg, .png, .mp3, .mp4, .wav, .webm"
            onChange={handleFileSelect}
          />
        </div>
        <SpellCheckTextArea
          rows={3}
          ref={inputRef}
          placeholder="Type diary entry..."
          className="w-full border focus:outline-none resize-none"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        {canSend && (
          <Button size="large" type="primary" onClick={submit}>
            Send
          </Button>
        )}
      </div>
    </div>
  );
};

export default DiaryComposer;
